import {
  FormHelperText,
  InputAdornment,
  TextField,
  styled,
} from "@mui/material";
import React from "react";
import { COLORS } from "../../assets/theme";

import { TextFieldProps } from "@mui/material";
import { ReactNode } from "react";

import { SxProps, Theme } from "@mui/material";
import CustomLabel from "./CustomLabel";

export type ICustomTextFieldProps = {
  label?: string;
  helperText?: string;
  fullWidth?: boolean;
  size?: "small" | "medium" | "large";
  error?: boolean;
  hidden?: boolean;
  endAdornment?: string;
  bgColor?: string;
  rows?: number;
  minHeight?: string;
  minWidth?: string;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  borderRadius?: string;
  validateOnChange?: boolean;
  allowWhitespaceOnly?: boolean;
  highlightLabelStar?: boolean;
  onValidation?: (error: boolean) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    sx?: SxProps<Theme>;

} & TextFieldProps;

const StyledTextField = styled(TextField)<{
  $bgColor?: string;
  $isMultiline?: boolean;
  $width?: string;
  $minHeight?: string;
  $minWidth?: string;
  $fullWidth?: boolean;
  $borderRadius?: string;
  $hidden?: boolean;
}>(
  ({
    $bgColor,
    $isMultiline,
    $width,
    $minHeight,
    $minWidth,
    $fullWidth,
    $borderRadius,
    $hidden,
  }) => ({
    backgroundColor: $bgColor || "transparent",
    width: $fullWidth ? "100%" : $width || "100%",
    minWidth: $minWidth || "250px",
    borderRadius: $borderRadius || "12px",
    visibility: $hidden ? "hidden" : "visible",
    "& .MuiOutlinedInput-root": {
      borderRadius: $borderRadius || "12px",
      height: $isMultiline ? "auto" : "40px",
      minHeight: $isMultiline ? $minHeight : "39px",
      "& fieldset": {
        borderColor: "#ccc",
      },
      "&:hover fieldset": {
        borderColor: "#868585",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#868585",
      },
      "&.Mui-focused": {
        outline: "0px solid #868585",
      },
    },
    "& .MuiInputBase-input": {
      padding: "10px 12px",
    },
  })
);

 const validateFieldValue = (
  value: string,
  type: "email"
): boolean => {
  if (type === "email") {
    const emails = value
      .split(";")
      .map((email) => email.trim())
      .filter(Boolean);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emails.length === 0 || emails.some((email) => !emailRegex.test(email));
  } else {
    return value.trim() === "";
  }
};

const CustomTextField: React.FC<
  ICustomTextFieldProps & { minHeight?: string; minWidth?: string }
> = (props) => {
  const {
    label,
    helperText,
    fullWidth = true,
    size = "small",
    error = false,
    bgColor = "transparent",
    rows,
    multiline = false,
    endAdornment,
    validateOnChange = false,
    allowWhitespaceOnly = false,
    onValidation,
    onBlur,
    onChange,
    minHeight,
    hidden = false,
    minWidth,
    startIcon,
    endIcon,
    highlightLabelStar = false,
    sx = {},
    onWheel,
    inputProps,
    ...rest
  } = props;

  const sizeWidthMap: Record<string, string> = {
    small: "250px",
    medium: "368px",
    large: "463px",
  };

  const calculatedMinWidth = minWidth || sizeWidthMap[size] || "100%";


  
  const handleValidation = (value: string) => {
    const isError = validateFieldValue(value, props.type as "email");
    onValidation?.(isError);
  };

  const isNumberType = props.type === "number";

  let effectiveMin: number | undefined;
  if (inputProps?.min !== undefined) {
    effectiveMin = Number(inputProps.min);
  } else if (isNumberType) {
    effectiveMin = 0;
  }
  // Runs before both inputProps.onChange (e.g. MUI Autocomplete's) and the TextField onChange
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!allowWhitespaceOnly && e.target.value !== "" && e.target.value.trim() === "") {
      e.target.value = "";
    }
    inputProps?.onChange?.(e);
  };

  const mergedInputProps = {
    ...inputProps,
    ...(isNumberType && effectiveMin !== undefined ? { min: effectiveMin } : {}),
    onChange: handleInputChange,
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => 
    {
      
      let value = e.target.value;
      if (
        isNumberType &&
        effectiveMin !== undefined &&
        value !== "" &&
        !Number.isNaN(Number(value)) &&
        Number(value) < effectiveMin
      ) {
        value = String(effectiveMin);
        e.target.value = value;
      }
    if (validateOnChange) {
      handleValidation(value);
    }
    onChange?.(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (props.onKeyDown) {
      props.onKeyDown(e);
    }
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (isNumberType) {
      (document.activeElement as HTMLElement | null)?.blur?.();
    }
    onWheel?.(e);
  };

  return (
    <div style={{ width: fullWidth ? "100%" : calculatedMinWidth }}>
      {label && !hidden && (
        <CustomLabel
          text={label}
          fontSize="14px"
          fontWeight={500}
          color={COLORS.typographyColor}
          highlightLabelStar={highlightLabelStar}
        />
      )}
      <StyledTextField
        size={size}
        fullWidth={fullWidth}
        error={error}
        multiline={multiline || !!rows}
        rows={rows}
        $isMultiline={multiline || !!rows}
        $bgColor={bgColor}
        $width={calculatedMinWidth}
        $minHeight={minHeight}
        $minWidth={calculatedMinWidth}
        $fullWidth={fullWidth}
        $hidden={hidden}
        onChange={handleChange}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        onWheel={handleWheel}
        inputProps={mergedInputProps}
        InputProps={{
          startAdornment: startIcon && (
            <InputAdornment position="start">{startIcon}</InputAdornment>
          ),
          endAdornment: (
            <>
              {endAdornment}
              {endIcon && (
                <InputAdornment position="end">{endIcon}</InputAdornment>
              )}
            </>
          ),
        }}
        sx={{
          ...sx,
        }}
        {...rest}
      />
      {!hidden && helperText && (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      )}
    </div>
  );
};

export default CustomTextField;
